import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import classNames from "classnames";
import {
  getPricedOrderItems,
  rejectOrderPricing,
  acceptOrderPricing,
} from "../../../../services/order";
import { PRICED_ORDER_ITEMS_CRUMBS } from "../../../../constants/crumbs";
import { ORDER_ITEMS_PRICES_COLUMNS } from "../../../../constants/table";
import { APP_PATHS } from "../../../../constants/paths";
import OrderItemPrice from "./OrderItemPrice/OrderItemPrice";
import Table from "../../../Table/Table";
import PortalBreadcrumb from "../../../UI/Breadcrumb/PortalBreadcrumb/PortalBreadcrumb";
import Loading from "../../../UI/Loading/Loading";
import EmptyContent from "../../../UI/EmptyContent/EmptyContent";
import ErrorMessage from "../../../UI/Error/ErrorMessage/ErrorMessage";
import PrimaryButton from "../../../UI/PrimaryButton/PrimaryButton";
import DangerBtn from "../../../UI/DangerBtn/DangerBtn";
import classes from "./PricedOrderItems.module.css";

const PricedOrderItems = () => {
  const { orderId } = useParams();
  const [pricedItems, setPricedItems] = useState([]);
  const [isPricedItemsLoading, setIsPricedItemsLoading] = useState(true);
  const [pricedItemsLoadingError, setPricedItemsLoadingError] = useState("");
  const [selectedPrices, setSelectedPrices] = useState({});
  const [isSubmitPricesLoading, setIsSubmitPricesLoading] = useState(false);
  const [submitPricesError, setSubmitPricesError] = useState("");
  const [isSubmitWithoutSelecting, setIsSubmitWithoutSelecting] =
    useState(false);
  const [isRejectPricesLoading, setIsRejectPricesLoading] = useState(false);
  const [rejectPricesError, setRejectPricesError] = useState("");
  const navigate = useNavigate();

  const selectedPricesChangedHandler = (orderItemId, e) => {
    const pricingObject = e.target.value;

    setSelectedPrices((prevSelectedPrices) => ({
      ...prevSelectedPrices,
      [orderItemId]: JSON.parse(pricingObject),
    }));
    setIsSubmitWithoutSelecting(false);
  };

  const pricesTableData = useMemo(() => {
    return pricedItems.map((pricedItem) => {
      const { orderItemId, name, pricesList } = pricedItem;
      return {
        name: <p className={classes["prices-table__item-name"]}>{name}</p>,
        prices: (
          <>
            <ul className={classes["prices-table__price-list"]}>
              {pricesList.map((priceRecord, index) => (
                <OrderItemPrice
                  key={index}
                  priceId={priceRecord.id}
                  supplierId={priceRecord.supplierId}
                  comment={priceRecord.pricingComment}
                  price={priceRecord.price}
                  suggestedSellingPrice={priceRecord.suggestedSellingPrice}
                  checked={
                    selectedPrices[orderItemId]?.selectedSupplierId ===
                    priceRecord.supplierId
                  }
                  onChange={(e) => selectedPricesChangedHandler(orderItemId, e)}
                  hasError={isSubmitWithoutSelecting}
                />
              ))}
            </ul>
            {isSubmitWithoutSelecting && (
              <p className={classes["prices-table__error-txt"]}>
                الرجاء تحديد سعر هذه القطعة لإستكمال الطلب
              </p>
            )}
          </>
        ),
      };
    });
    // eslint-disable-next-line
  }, [
    pricedItems,
    selectedPrices,
    isSubmitWithoutSelecting,
    isSubmitPricesLoading,
  ]);

  const rejectPricesHandler = async () => {
    try {
      setIsRejectPricesLoading(true);
      setSubmitPricesError("");
      setRejectPricesError("");
      await rejectOrderPricing({ orderId });
      navigate(APP_PATHS.HOME.url);
    } catch (err) {
      setRejectPricesError("حدث خطأ اثناء رفض الأسعار !");
    } finally {
      setIsRejectPricesLoading(false);
    }
  };

  const submitPricesHandler = async () => {
    if (Object.keys(selectedPrices).length === 0) {
      setIsSubmitWithoutSelecting(true);
      return;
    }
    try {
      setIsSubmitPricesLoading(true);
      setSubmitPricesError("");
      setRejectPricesError("");
      const requestPayload = {
        orderItemList: [],
      };
      for (const orderItemId in selectedPrices) {
        requestPayload.orderItemList.push({
          selected: true,
          orderItemId: Number(orderItemId),
          selectedSupplierId: selectedPrices[orderItemId].selectedSupplierId,
          sellingPrice: selectedPrices[orderItemId].price,
          suggestedSellingPrice:
            selectedPrices[orderItemId].suggestedSellingPrice,
        });
      }

      await acceptOrderPricing(requestPayload);
      navigate(APP_PATHS.HOME.url);
    } catch (err) {
      setSubmitPricesError("حدث خطأ اثناء إنشاء الطلب !");
    } finally {
      setIsSubmitPricesLoading(false);
    }
  };

  useEffect(() => {
    const fetchPricedOrderItems = async () => {
      try {
        setIsPricedItemsLoading(true);
        const response = await getPricedOrderItems(Number(orderId));
        const {
          data: { orderItemList },
        } = response;
        setPricedItems(orderItemList);
      } catch (err) {
        setPricedItemsLoadingError("! حدث خطأ أثناء تحميل محتويات الطلب");
      } finally {
        setIsPricedItemsLoading(false);
      }
    };
    if (Number(orderId)) {
      fetchPricedOrderItems();
    } else {
      setIsPricedItemsLoading(false);
    }
  }, [orderId]);

  return (
    <div className={classes["priced-order-items"]}>
      <PortalBreadcrumb paths={PRICED_ORDER_ITEMS_CRUMBS} />
      {isPricedItemsLoading ? (
        <Loading className={classes["priced-order-items__loading"]} />
      ) : pricedItems.length === 0 || pricedItemsLoadingError ? (
        <EmptyContent
          className={classes["priced-order-items__empty"]}
          message="لا يوجد قطع مسعرة لعرضها !"
          withAction
        />
      ) : (
        <>
          <Table columns={ORDER_ITEMS_PRICES_COLUMNS} data={pricesTableData} />
          <div className={classes["prices-table__reject-wrapper"]}>
            <DangerBtn
              className={classes["prices-table__reject-btn"]}
              disabled={isSubmitPricesLoading || isRejectPricesLoading}
              onClick={rejectPricesHandler}
            >
              {isRejectPricesLoading ? <Loading /> : "رفض جميع الأسعار"}
            </DangerBtn>
            {rejectPricesError && <ErrorMessage message={rejectPricesError} />}
          </div>
          <div className={classes["priced-order-items__submit-prices-wrapper"]}>
            <PrimaryButton
              withAnimation={!isSubmitPricesLoading}
              className={classNames(
                classes["priced-order-items__primary-btn"],
                {
                  [classes["priced-order-items__primary-btn--loading"]]:
                    isSubmitPricesLoading,
                }
              )}
              onClick={submitPricesHandler}
              disabled={isSubmitPricesLoading || isRejectPricesLoading}
            >
              {isSubmitPricesLoading ? <Loading /> : "إنشاء طلب"}
            </PrimaryButton>
            {submitPricesError && <ErrorMessage message={submitPricesError} />}
          </div>
        </>
      )}
    </div>
  );
};

export default PricedOrderItems;
